import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowDown, ArrowUp, Loader2, X } from 'lucide-react';
import { MAX_UPLOAD_LABEL } from '@designdeck/shared';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import { translateErrorCode } from '@/lib/api-errors';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { cn } from '@/lib/utils';
import type { BulkCell } from '@/stores/bulkCreationStore';
import { DIMENSIONS_UNREADABLE_CODE } from './useMotifUploads';

/** Params the upload error texts interpolate; the cell keeps only the code. */
const UPLOAD_ERROR_PARAMS = {
  limit: MAX_UPLOAD_LABEL,
  allowed: 'PNG, JPG, SVG',
};

export interface MotifCellMoveControls {
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export interface MotifCellProps {
  cell: BulkCell;
  rowNumber: number;
  /** Local file while the cell uploads, for an instant preview. */
  localFile?: File;
  /** A back that no active product can take (R15). */
  ignored?: boolean;
  onRemove: () => void;
  /** Arrows of a back cell (R21). */
  moveControls?: MotifCellMoveControls;
}

/**
 * Object URL of a local file, revoked as soon as the preview is no longer
 * shown (cell ready, removed or unmounted). Created in an effect so a
 * StrictMode remount gets a fresh URL instead of a revoked one.
 */
function useObjectUrl(file: File | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

/**
 * One motif in the table (R19, R21, R58): thumbnail, file name and the pixel
 * size the server measured, or the upload spinner, or the translated error
 * with the remove button. Back cells carry the arrows to the neighbour rows.
 */
export function MotifCell({
  cell,
  rowNumber,
  localFile,
  ignored = false,
  onRemove,
  moveControls,
}: MotifCellProps) {
  const { t } = useTranslation();
  const isUploading = cell.state === 'uploading';
  const localUrl = useObjectUrl(isUploading ? localFile : undefined);
  const previewUrl = isUploading
    ? localUrl
    : cell.state === 'ready'
      ? proxyStorageUrl(cell.url)
      : null;

  const errorText =
    cell.state === 'error'
      ? cell.errorCode === DIMENSIONS_UNREADABLE_CODE
        ? t('seller.bulk.motifs.cell.dimensionsUnreadable')
        : (translateErrorCode(cell.errorCode, UPLOAD_ERROR_PARAMS) ??
          t('seller.bulk.motifs.cell.uploadFailed'))
      : null;

  return (
    <div
      data-testid={`bulk-cell-${cell.id}`}
      data-state={cell.state}
      className="flex min-w-[14rem] items-center gap-3"
    >
      <div
        className={cn(
          'relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-chip bg-paper-3 ring-1 ring-line',
          ignored && 'opacity-50'
        )}
      >
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full object-contain"
          />
        )}
        {isUploading && (
          <span className="absolute inset-0 grid place-items-center bg-paper/60">
            <Loader2
              aria-hidden="true"
              className="h-4 w-4 animate-spin text-ink-2"
            />
          </span>
        )}
        {cell.state === 'error' && (
          <AlertCircle aria-hidden="true" className="h-5 w-5 text-danger" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'truncate text-sm font-medium text-ink',
            ignored && 'text-mute line-through'
          )}
          title={cell.fileName}
        >
          {cell.fileName}
        </span>
        {isUploading && (
          <span
            role="status"
            aria-live="polite"
            className="font-mono text-[12px] text-mute"
          >
            {t('seller.bulk.motifs.cell.uploading')}
          </span>
        )}
        {ignored && cell.state !== 'error' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="w-fit font-mono text-[12px] text-warning underline decoration-dotted"
              >
                {t('seller.bulk.motifs.backIgnored')}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t('seller.bulk.motifs.backIgnoredHint')}
            </TooltipContent>
          </Tooltip>
        )}
        {!ignored &&
          cell.state === 'ready' &&
          cell.width !== undefined &&
          cell.height !== undefined && (
            <span className="font-mono text-[12px] text-mute">
              {t('seller.bulk.motifs.cell.dimensions', {
                width: cell.width,
                height: cell.height,
              })}
            </span>
          )}
        {errorText && (
          <span role="alert" className="text-[12.5px] text-danger">
            {errorText}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {moveControls && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={!moveControls.canMoveUp}
              onClick={moveControls.onMoveUp}
              aria-label={t('seller.bulk.motifs.moveBackUp', {
                number: rowNumber,
              })}
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={!moveControls.canMoveDown}
              onClick={moveControls.onMoveDown}
              aria-label={t('seller.bulk.motifs.moveBackDown', {
                number: rowNumber,
              })}
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </Button>
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onRemove}
          aria-label={t('seller.bulk.motifs.cell.remove', {
            fileName: cell.fileName,
          })}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
